import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { formatCount, formatShare } from "../../charts/charts.utils";
import NotFilterableByOfferHint from "../../charts/NotFilterableByOfferHint";
import StatCard, { EMPTY_VALUE } from "../../kpi/StatCard";
import {
  offerUnsubscriptionsGroupByOfferQuery,
  trialUnsubscriptionsViewsQuery,
  unsubscribeReasonsQuery,
} from "../offerAnalytics.queries";
import {
  buildUnsubscribeReasons,
  getTopReason,
  summarizeUnsubscriptions,
  sumTrialUnsubscriptions,
} from "../offerAnalytics.utils";

import type { JSX } from "react";

import type { OfferAnalyticsFilterProps } from "../offerAnalytics.types";

function UnsubscriptionsKpiGrid({
  filters,
}: OfferAnalyticsFilterProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;

  const unsubscriptionsResult = useQuery(
    offerUnsubscriptionsGroupByOfferQuery(projectSlug, filters),
  );
  const reasonsResult = useQuery(
    unsubscribeReasonsQuery(projectSlug, filters, lang),
  );
  const trialResult = useQuery(
    trialUnsubscriptionsViewsQuery(projectSlug, filters),
  );

  const summary = useMemo(
    () => summarizeUnsubscriptions(unsubscriptionsResult.data?.data ?? []),
    [unsubscriptionsResult.data],
  );
  const trialTotal = useMemo(
    () => sumTrialUnsubscriptions(trialResult.data?.data ?? []),
    [trialResult.data],
  );
  const topReason = useMemo(
    () =>
      getTopReason(
        buildUnsubscribeReasons(reasonsResult.data, lang, (reason) =>
          t(`unsubscribe_reasons.${reason}`),
        ),
      ),
    [reasonsResult.data, lang, t],
  );

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
      <StatCard
        label={t("offerAnalytics.kpi.unsubscriptions")}
        query={unsubscriptionsResult}
        scope="period"
        value={formatCount(summary.total, lang)}
      />
      <StatCard
        label={t("offerAnalytics.kpi.voluntaryShare")}
        query={unsubscriptionsResult}
        scope="period"
        footer={t("offerAnalytics.kpi.voluntaryShareFooter", {
          voluntary: formatCount(summary.voluntary, lang),
          automatic: formatCount(summary.automatic, lang),
        })}
        value={
          summary.total
            ? formatShare(summary.voluntary / summary.total, lang)
            : EMPTY_VALUE
        }
      />
      <StatCard
        footer={filters.offerUuid ? <NotFilterableByOfferHint /> : undefined}
        label={t("offerAnalytics.kpi.trialUnsubscriptions")}
        query={trialResult}
        scope="period"
        value={formatCount(trialTotal, lang)}
      />
      <StatCard
        label={t("offerAnalytics.kpi.topReason")}
        query={reasonsResult}
        scope="period"
        value={topReason?.label ?? EMPTY_VALUE}
        valueTitle={topReason?.label}
        footer={
          topReason
            ? t("offerAnalytics.kpi.topReasonFooter", {
                share: formatShare(topReason.share, lang),
              })
            : undefined
        }
      />
    </div>
  );
}

export default UnsubscriptionsKpiGrid;
